import { FormattedDate, FormattedDateTime, Icon, ImageWithAspectRatio, Label } from "@dgs/core";
import { isSameDay, parseISO } from "date-fns";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import styled from "styled-components";
import { EventRating } from "../../../../shared/events/EventRating";
import { ParticipationStatusLabel } from "../../../../shared/events/eventUsers/ParticipationStatusLabel";
import { EventListOverviewResource } from "../../../../shared/types/event";
import { DetailsPageInfoField } from "../../../../shared/ui/DetailsPageInfoField";

const EventCardWrapper = styled.div`
	background: ${({ theme }) => theme.colors.palette.content.main.base};
	${({ theme }) => theme.borderRadius.large};
	display: grid;
	grid-template-rows: min-content 1fr;
	overflow: hidden;
`;
const ImageContainer = styled.div`
	position: relative;
`;
const EventCardContainer = styled.div`
	padding: ${({ theme }) => theme.spacing(4)};
	display: grid;
	grid-template-rows: min-content min-content 1fr;
`;
const EventCardHeader = styled.div`
	display: grid;
	grid-template-columns: 1fr min-content;
	gap: ${({ theme }) => theme.spacing(1)};
`;
const EventCardHeaderLeading = styled.div`
	display: grid;
	gap: ${({ theme }) => theme.spacing(1)};
`;
const EventCardName = styled.div`
	font-weight: bold;
	min-height: 40px;
	line-height: 1.25rem;
	display: grid;
	grid-template-columns: 1fr min-content;
`;

const EventCardHeaderRow = styled.div`
	display: grid;
	grid-template-columns: min-content 1fr;
	align-items: center;
	gap: ${({ theme }) => theme.spacing(1)};
`;
const EventCardHeaderIcon = styled(Icon)`
	height: 16px;
	width: 16px;
`;
const Divider = styled.div`
	border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grey[30]};
	margin: ${({ theme }) => theme.spacing(2)} 0;
`;
const EventCardContent = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	align-content: space-between;
`;
const Badges = styled.div`
	position: absolute;
	display: flex;
	gap: ${({ theme }) => theme.spacing(2)};
	top: ${({ theme }) => theme.spacing(2)};
	left: ${({ theme }) => theme.spacing(2)};
`;
const HighlightIcon = styled(Icon)`
	height: 16px;
	width: 16px;
`;
const StyledLink = styled(Link)`
	color: inherit;
	text-decoration: none;
	display: grid;
`;

interface Props {
	event: EventListOverviewResource;
}
export const EventOverviewCard: FC<Props> = ({ event }) => {
	const { t } = useTranslation();
	return (
		<StyledLink to={`/events/overview/${event.id}`}>
			<EventCardWrapper>
				<ImageContainer>
					<ImageWithAspectRatio image={event.image} alt={event.image?.name} />
					<Badges>
						{event.isHighlight && (
							<Label color="primary">
								<HighlightIcon $icon="star_filled" />
								{t("Highlight")}
							</Label>
						)}
						<ParticipationStatusLabel status={event.participationStatus} />
					</Badges>
				</ImageContainer>
				<EventCardContainer>
					<EventCardHeader>
						<EventCardHeaderLeading>
							<EventCardName>
								{event.name}
								<EventRating rating={event.rating} />
							</EventCardName>
							<EventCardHeaderRow>
								<EventCardHeaderIcon $icon="calendar" />
								{isSameDay(event.start, event.end) ? (
									<FormattedDate date={parseISO(event.start)} />
								) : (
									<div>
										<FormattedDate date={parseISO(event.start)} /> - <FormattedDate date={parseISO(event.end)} />
									</div>
								)}
							</EventCardHeaderRow>
							<EventCardHeaderRow>
								<EventCardHeaderIcon $icon="schedule" />
								<FormattedDateTime dateTime={event.start} format={{ timeStyle: "short" }} />
							</EventCardHeaderRow>
						</EventCardHeaderLeading>
					</EventCardHeader>
					<Divider />
					<EventCardContent>
						<DetailsPageInfoField
							title={t("Location")}
							value={event.address !== null ? `${event.address.city}, ${event.address.country.name}` : "-"}
						/>
						<DetailsPageInfoField title={t("Event type")} value={event.eventType.name} />
					</EventCardContent>
					<DetailsPageInfoField
						title={t("Project managers")}
						hoverTitle={event.projectManagers.map((v) => v.name).join(", ")}
						value={
							event.projectManagers.length > 0
								? `${event.projectManagers
										.slice(0, 3)
										.map((value) => `${value.name}`)
										.join(", ")}${event.projectManagers.length > 3 ? "..." : ""}`
								: "-"
						}
					/>
				</EventCardContainer>
			</EventCardWrapper>
		</StyledLink>
	);
};
